import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, FileText, Video, Headphones, Download, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';

const resources = [
  // Digital Resources
  {
    id: '1',
    type: 'digital',
    title: 'JSTOR Academic Database',
    description: 'Access thousands of academic journals, books, and primary sources.',
    category: 'Research',
    icon: FileText,
    link: '#'
  },
  {
    id: '2',
    type: 'digital',
    title: 'LinkedIn Learning',
    description: 'Video courses in software, design and business skills.',
    category: 'Learning',
    icon: Video,
    link: '#'
  },
  {
    id: '3',
    type: 'digital',
    title: 'OverDrive/Libby',
    description: 'Borrow e-books and digital audiobooks from our digital collection.',
    category: 'E-books',
    icon: BookOpen,
    link: '#'
  },
  {
    id: '4',
    type: 'digital',
    title: 'Audible Collection',
    description: 'Stream audiobooks directly from our Audible partnership collection.',
    category: 'Audiobooks',
    icon: Headphones,
    link: '#'
  },
  
  // Educational Resources
  {
    id: '5',
    type: 'educational',
    title: 'Khan Academy Lessons',
    description: 'Free learning resources for mathematics, science, computing, history, and more.',
    category: 'All Subjects',
    icon: Video,
    link: '#'
  },
  {
    id: '6',
    type: 'educational',
    title: 'Science Lab Materials',
    description: 'Downloadable lab worksheets and experiment guides for science courses.',
    category: 'Science',
    icon: Download,
    link: '#'
  },
  {
    id: '7',
    type: 'educational',
    title: 'Historical Archives',
    description: 'Primary source documents from significant historical periods.',
    category: 'History',
    icon: FileText,
    link: '#'
  },
  {
    id: '8',
    type: 'educational',
    title: 'Language Learning Resources',
    description: 'Interactive tools and materials for learning foreign languages.',
    category: 'Languages',
    icon: Headphones,
    link: '#'
  },
  
  // Research Resources
  {
    id: '9',
    type: 'research',
    title: 'ProQuest Research Database',
    description: 'Dissertations, newspapers and periodicals, searchable in one place.',
    category: 'Database',
    icon: FileText,
    link: '#'
  },
  {
    id: '10',
    type: 'research',
    title: 'Citation Management Tools',
    description: 'Resources for managing citations and creating bibliographies.',
    category: 'Writing',
    icon: FileText,
    link: '#'
  },
  {
    id: '11',
    type: 'research',
    title: 'Statistical Data Resources',
    description: 'Access to major statistical databases and data visualization tools.',
    category: 'Data',
    icon: FileText,
    link: '#'
  },
  {
    id: '12',
    type: 'research',
    title: 'Research Methodology Guides',
    description: 'Guides to research methods, subject by subject.',
    category: 'Methodology',
    icon: FileText,
    link: '#'
  }
];

const ResourceCard = ({ resource }: { resource: typeof resources[0] }) => {
  return (
    <Card className="overflow-hidden hover:shadow-md transition-all h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="mb-2 w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
          <resource.icon className="h-5 w-5 text-primary" />
        </div>
        <CardTitle className="text-lg">{resource.title}</CardTitle>
        <CardDescription className="text-sm">{resource.category}</CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <p className="text-muted-foreground text-sm">{resource.description}</p>
      </CardContent>
      <CardFooter className="pt-2">
        <Button variant="outline" size="sm" className="w-full">
          Access Resource
          <ExternalLink className="ml-2 h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

const Resources: React.FC = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Header />
      
      {/* Hero Banner */}
      <section className="relative mt-20 pt-16 pb-20 px-6 md:px-12 bg-gradient-to-r from-primary/90 to-purple/80 text-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Library Resources</h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Databases, e-books, audiobooks and study guides: everything the library subscribes to, in one list.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Resources Section */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="digital" className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="grid w-full max-w-md grid-cols-3">
                <TabsTrigger value="digital">Digital</TabsTrigger>
                <TabsTrigger value="educational">Educational</TabsTrigger>
                <TabsTrigger value="research">Research</TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="digital">
              <motion.div 
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                {resources
                  .filter(resource => resource.type === 'digital')
                  .map(resource => (
                    <motion.div key={resource.id} variants={fadeIn} transition={{ duration: 0.5 }}>
                      <ResourceCard resource={resource} />
                    </motion.div>
                  ))
                }
              </motion.div>
            </TabsContent>
            
            <TabsContent value="educational">
              <motion.div 
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                {resources
                  .filter(resource => resource.type === 'educational')
                  .map(resource => (
                    <motion.div key={resource.id} variants={fadeIn} transition={{ duration: 0.5 }}>
                      <ResourceCard resource={resource} />
                    </motion.div>
                  ))
                }
              </motion.div>
            </TabsContent>
            
            <TabsContent value="research">
              <motion.div 
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                {resources
                  .filter(resource => resource.type === 'research')
                  .map(resource => (
                    <motion.div key={resource.id} variants={fadeIn} transition={{ duration: 0.5 }}>
                      <ResourceCard resource={resource} />
                    </motion.div>
                  ))
                }
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      
      {/* Help Section */}
      <section className="py-12 px-6 md:px-12 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">Stuck on any of these?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              A librarian can get you into any of them, or point you at something better.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="default">
                Schedule Consultation
              </Button>
              <Button variant="outline">
                View Tutorial Videos
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Resources;
